// @ts-nocheck
import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('change', this.variantChanged.bind(this));
    
    // Inicializar filtrado dinámico después de que se cargue el componente
    //setTimeout(() => {
    //  this.initializeDynamicFiltering();
    //}, 100);
  }

  /**
   * Inicializa el sistema de filtrado dinámico de opciones
   */
  initializeDynamicFiltering() {
    // Obtener todas las variantes del producto desde el JSON existente
    const scriptTag = this.querySelector('script[type="application/json"]');
    if (!scriptTag) return;
    
    try {
      const productData = JSON.parse(scriptTag.textContent);
      this.allVariants = productData.variants || [];
      
      // Aplicar filtrado inicial
      this.filterAvailableOptions();
      
      console.log('Dynamic filtering initialized with', this.allVariants.length, 'variants');
    } catch (error) {
      console.error('Error initializing dynamic filtering:', error);
    }
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: event.target.dataset.optionValueId ?? '' }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = event.target.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    this.fetchUpdatedSection(this.buildRequestUrl(event.target), loadsNewProduct);

    const url = new URL(window.location.href);

    let variantId;

    if (event.target instanceof HTMLInputElement && event.target.type === 'radio') {
      variantId = event.target.dataset.variantId || null;
    } else if (event.target instanceof HTMLSelectElement) {
      const selectedOption = event.target.options[event.target.selectedIndex];
      variantId = selectedOption?.dataset.variantId || null;
    }

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      history.replaceState({}, '', url.toString());
    }

    // NUEVO: Aplicar filtrado dinámico después del cambio
    //setTimeout(() => {
    //  this.filterAvailableOptions();
    //}, 50);
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
            this.handleCustomVariantUpdate(JSON.parse(textContent), html, newProduct);
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.log('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }

  /**
   * Handles custom variant update.
   * @param {Object} variant - The variant object.
   * @param {Document} html - The new HTML.
   * @param {NewProduct | undefined} newProduct - Information about the new product if it has changed, otherwise undefined.
   */
  handleCustomVariantUpdate(variant, html, newProduct) {    
    // Actualizar datos de variantes relacionadas si existe la funcionalidad
    if (variant && variant.id) {
      this.updateSiblingVariantData(variant.id, variant);
      setTimeout(() => {
        this.updateVariantReference(variant);
      }, 100);
    } else {
      const multistepButton = document.querySelector('.ce-multistep-trigger[data-multistep]');
      if (multistepButton) {
        multistepButton.style.display = 'none';
      }
    }
  }


  /**
   * Updates variant reference block.
   * @param {Object} variant - The variant object.
   */
  updateVariantReference(variant) {
    const variantRefElement = document.querySelector('variant-ref');
    if (!variantRefElement || !variant) return;
    
    // Obtener datos de metafields desde el JSON existente
    const variantMetafields = this.getVariantMetafields();
    const variantData = variantMetafields.find(v => String(v.variantId) === String(variant.id));
    
    if (!variantData) {
      console.warn('No variant data found for variant ID:', variant.id);
      variantRefElement.style.display = 'none';
      return;
    }
    
    // Obtener el modelo de la variante desde tu JSON
    const variantModel = variantData.variantModel;
    
    if (variantModel && variantModel.trim() !== '') {
      // Obtener las traducciones desde el tema
      const modelLabel = window.Theme?.translations?.variant_model || 
                        'Referencia:';
      
      // Actualizar el contenido del elemento
      variantRefElement.innerHTML = `${modelLabel} ${variantModel}`;
      
      // Mostrar el elemento si estaba oculto
      variantRefElement.style.display = 'block';
      variantRefElement.classList.remove('hidden');

    } else {
      // Ocultar el elemento si no hay modelo
      variantRefElement.style.display = 'none';
      variantRefElement.classList.add('hidden');
      
    }
  }

  /**
   * Gets variant metafields from the DOM.
   * @returns {Array} Array of variant metafields.
   */
  getVariantMetafields() {
    if (!this.variantMetafields) {
      const metafieldsElement = document.getElementById('variant-metafields');
      if (metafieldsElement && metafieldsElement.textContent) {
        try {
          this.variantMetafields = JSON.parse(metafieldsElement.textContent);
        } catch (error) {
          console.warn('Error parsing variant metafields:', error);
          this.variantMetafields = [];
        }
      } else {
        this.variantMetafields = [];
      }
    }
    return this.variantMetafields;
  }

  /**
   * Updates sibling variant data based on current variant ID.
   * @param {string|number} currentVariantId - The current variant ID.
   */
  updateSiblingVariantData(currentVariantId, currentVariant) {
    const variantMetafields = this.getVariantMetafields();
    const relatedVariant = variantMetafields.find(variant => 
      String(variant.variantId) === String(currentVariantId)
    );
    
    if (relatedVariant) {
      this.updateDataSets(
        relatedVariant.relatedVariantId, 
        relatedVariant.relatedVariantPrice, 
        relatedVariant.relatedVariantSku, 
        relatedVariant.relatedVariantImage, 
        relatedVariant.relatedVariantAvailable, 
        relatedVariant.multistep,
        currentVariant
      );
      
      // Ejecutar función global si existe
      if (window.goToFirstStep && typeof window.goToFirstStep === 'function') {
        window.goToFirstStep();
      }
    }
  }

  /**
   * Updates data sets for related variants.
   * @param {string} siblingVariantId - Sibling variant ID.
   * @param {string} siblingVariantPrice - Sibling variant price.
   * @param {string} siblingVariantSku - Sibling variant SKU.
   * @param {string} siblingVariantImage - Sibling variant image.
   * @param {string} siblingVariantAvailable - Sibling variant availability.
   * @param {string} multistep - Multistep data.
   */
  updateDataSets(siblingVariantId, siblingVariantPrice, siblingVariantSku, siblingVariantImage, siblingVariantAvailable, multistep, currentVariant) {
    const labelsToUpdate = document.querySelectorAll('label.ce-option');
    let optionPrice = 0;
    const multistepButton = document.querySelector('.ce-multistep-trigger[data-multistep]');

    if (currentVariant && currentVariant.available) {
      multistepButton.style.display = 'block';
    } else {
      multistepButton.style.display = 'none';
    }

    // Actualizar título de variante si existe
    const variantLabel = document.querySelector(`#variant-radios-${this.dataset.section} .form__label`);
    if (variantLabel && currentVariant) {
      variantLabel.textContent = currentVariant.title;
    }

    labelsToUpdate.forEach(label => {
      // Mostrar/ocultar elementos relacionados con gafas de sol
      /*
      if (label.dataset.gesvisionValue !== undefined && 
          label.dataset.gesvisionValue.includes('sunglasses') && 
          siblingVariantId !== "" && 
          siblingVariantAvailable === 'true') {
        label.style.display = 'grid';
      } else {
        if (label.dataset.gesvisionValue !== undefined && 
            label.dataset.gesvisionValue.includes('_sunglasses')) {
          label.style.display = 'none';
        }
      }
      */
      
      // Actualizar datasets con información de la variante actual
      if (currentVariant) {
        if (label.dataset.currentId !== undefined) {
          label.dataset.currentId = currentVariant.id;
        }
        if (label.dataset.currentTitle !== undefined) {
          label.dataset.currentTitle = currentVariant.title;
        }
        if (label.dataset.currentPrice !== undefined) {
          label.dataset.currentPrice = currentVariant.price;
        }
        if (label.dataset.currentSku !== undefined) {
          label.dataset.currentSku = currentVariant.sku;
        }
        if (label.dataset.currentImage !== undefined) {
          label.dataset.currentImage = siblingVariantImage;
        }
      }
      
      // Actualizar datasets con información de variante alternativa
      if (label.dataset.alternativeId !== undefined) {
        label.dataset.alternativeId = siblingVariantId;
      }
      if (label.dataset.alternativePrice !== undefined) {
        label.dataset.alternativePrice = siblingVariantPrice;
      }
      if (label.dataset.alternativeSku !== undefined) {
        label.dataset.alternativeSku = siblingVariantSku;
      }
      
      // Calcular y actualizar precio de opción
      const addonsPrice = label.dataset.addonsPrice ? parseFloat(label.dataset.addonsPrice) : 0;
      const alternativePrice = label.dataset.alternativePrice ? parseFloat(label.dataset.alternativePrice) : 0;
      
      if (label.dataset.alternativePrice !== undefined) {
        optionPrice = parseFloat(alternativePrice / 100) + (addonsPrice / 100);
      } else if (currentVariant) {
        optionPrice = parseFloat(currentVariant.price / 100) + (addonsPrice / 100);
      }
      
      const priceEl = label.querySelector('.option-price.option-product__price');
      if (priceEl) {
        priceEl.textContent = `$ ${Math.ceil(optionPrice)}`;
      }
    });
  }

  /**
   * Helper function to convert string to handle format.
   * @param {string} str - String to handleize.
   * @returns {string} Handleized string.
   */
  handleize(str) {
    return str.toLowerCase().replace(/\s+/g, '-').replace(/^-+|-+$/g, '');
  }

  /**
   * Filtra las opciones disponibles según las variantes seleccionadas
   */
  filterAvailableOptions() {
    // Obtener todas las opciones del selector
    const options = Array.from(this.querySelectorAll('select option'));

    // Filtrar opciones según las variantes seleccionadas
    options.forEach(option => {
      const optionValueId = option.dataset.optionValueId;
      const variantId = option.dataset.variantId;

      // Verificar si la opción está disponible según las variantes seleccionadas
      if (this.allVariants.find(variant => String(variant.id) === String(variantId))) {
        option.style.display = 'block';
      } else {
        option.style.display = 'none';
      }
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
